import { useEffect, useMemo, useRef, useState } from 'react';
import { chevronPoints, lineZone } from '@urbanrats/domain';
import type { RoundSnapshot } from '@urbanrats/contracts';
import type { ClientGateways, PlaybackTimelineEvent } from '../gateway/types.ts';
import { useI18n } from '../i18n/index.ts';
import { DEFAULT_INTRO, introFrame, locationLines, ZOOM, type IntroTiming, type LonLat } from '../lib/introTimeline.ts';
import { expectedPosition, revealed, syncAction } from '../lib/playbackSync.ts';
import type { RoundOutcome } from '../useGame.ts';
import { Globe } from './Globe.tsx';
import { LiveRelayStage, type PictureClock } from './LiveRelayStage.tsx';

/**
 * Live-camera round stage. Before playback: the globe rests on the camera with its location card.
 * From (playback start − intro length): the intro runs as a pure function of the server clock, the round
 * media (our HLS, never the camera's own stream) is preloaded in parallel and cross-faded in; at the
 * server's playback start the video plays. The camera URL is never known to this client.
 */
const EARTH_VIEW: LonLat = { lon: 10, lat: 25 };
const MAX_RETRIES = 4;
const backoff = (n: number) => Math.min(8000, 500 * 2 ** n);


export function LiveStage(p: Parameters<typeof DelayedLiveStage>[0] & { onPicture?: (c: PictureClock) => void; onIntro?: (playing: boolean) => void; outcome?: RoundOutcome | null; soundOn?: boolean }) {
  // LIVE: our relay of the camera with the live counter's boxes; DELAYED_LIVE: intro, then the locked footage.
  if (p.round.manifest.sourceMode === 'LIVE') return <LiveRelayStage round={p.round} gw={p.gw} outcome={p.outcome} soundOn={p.soundOn} reducedMotion={p.reducedMotion} onLiveCount={p.onLiveCount} onPicture={p.onPicture} onIntro={p.onIntro} />;
  return <DelayedLiveStage {...p} />;
}

function DelayedLiveStage({
  round,
  gw,
  timing = DEFAULT_INTRO,
  reducedMotion,
  onLiveCount,
}: {
  round: RoundSnapshot;
  gw: ClientGateways;
  timing?: IntroTiming;
  reducedMotion: boolean;
  /** Running count shown in sync with the footage (server-decided events), null outside playback. */
  onLiveCount?: (n: number | null) => void;
}) {
  const { t } = useI18n();
  const cam = round.manifest.camera!;
  const target = useMemo<LonLat | null>(() => (cam.latitude !== null && cam.longitude !== null ? { lon: cam.longitude, lat: cam.latitude } : null), [cam.latitude, cam.longitude]);
  const lines = locationLines(cam);
  const [now, setNow] = useState(() => gw.serverNowMs());
  const [media, setMedia] = useState<{ url: string; startAt: number; durationMs: number; events: PlaybackTimelineEvent[] } | null>(null);
  const [sync, setSync] = useState<{ drift: number; rate: number; seeks: number; videoPos: number | null }>({ drift: 0, rate: 1, seeks: 0, videoPos: null });
  const video = useRef<HTMLVideoElement>(null);
  // Picture size of the footage: the overlay uses the same box as the video (object-fit: contain).
  const [dims, setDims] = useState<{ w: number; h: number } | null>(null);
  const hlsRef = useRef<import('hls.js').default | null>(null);
  const startAt = round.playback ? Date.parse(round.playback.startAt) : null;
  const introStart = startAt !== null ? startAt - timing.playingAt : null;

  // One clock for everything: requestAnimationFrame reading the server-synchronised time.
  useEffect(() => {
    let raf = 0;
    const tick = () => {
      setNow(gw.serverNowMs());
      raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [gw]);

  // Preload as soon as the playback is scheduled (not when the globe is done), so there is no black gap.
  // `reload` re-requests the playback (new signed token) after a network failure or an expired token.
  const [reload, setReload] = useState(0);
  const [videoState, setVideoState] = useState<'OK' | 'RETRYING' | 'FAILED'>('OK');
  const [recoveries, setRecoveries] = useState(0);
  const failures = useRef(0);
  useEffect(() => {
    if (!round.playback || !gw.media.getLivePlayback) return;
    let live = true;
    let timer: ReturnType<typeof setTimeout> | undefined;
    const attempt = (n: number) =>
      void gw.media
        .getLivePlayback!(round.gameRoundId)
        .catch(() => null)
        .then((p) => {
          if (!live) return;
          if (p) setMedia({ url: p.playlistUrl, startAt: Date.parse(p.startAt), durationMs: Date.parse(p.revealAt) - Date.parse(p.startAt), events: p.events ?? [] });
          else if (n < MAX_RETRIES) timer = setTimeout(() => attempt(n + 1), backoff(n));
          else setVideoState('FAILED');
        });
    attempt(0);
    return () => {
      live = false;
      clearTimeout(timer);
    };
  }, [gw, round.gameRoundId, round.playback, reload]);
  useEffect(() => {
    const v = video.current;
    if (!v || !media) return;
    let dead = false;
    let timer: ReturnType<typeof setTimeout> | undefined;
    void import('hls.js').then(({ default: Hls }) => {
      if (dead) return;
      if (!Hls.isSupported()) {
        v.src = media.url;
        return;
      }
      // Late joiner: load from where the server timeline is now, not from 0.
      const startPosition = expectedPosition(gw.serverNowMs(), media.startAt, media.durationMs);
      const h = new Hls({ maxBufferLength: 30, fragLoadingMaxRetry: 3, manifestLoadingMaxRetry: 3, startPosition: startPosition > 0.5 ? startPosition : -1 });
      hlsRef.current = h;
      let mediaRecoveries = 0;
      h.on(Hls.Events.FRAG_LOADED, () => {
        failures.current = 0;
        setVideoState('OK');
      });
      // Recovery: a media error is recovered in place (twice); a network error re-requests the signed
      // playback (a new token, in case it expired) with backoff; after MAX_RETRIES the video is given up
      // honestly. The count and result never depend on it: they come from the server timeline.
      h.on(Hls.Events.ERROR, (_e, d) => {
        if (!d.fatal) return;
        if (d.type === Hls.ErrorTypes.MEDIA_ERROR && mediaRecoveries < 2) {
          mediaRecoveries++;
          h.recoverMediaError();
          return;
        }
        failures.current++;
        if (failures.current > MAX_RETRIES) {
          setVideoState('FAILED');
          return;
        }
        setVideoState('RETRYING');
        setRecoveries((n) => n + 1);
        timer = setTimeout(() => setReload((n) => n + 1), backoff(failures.current - 1));
      });
      h.loadSource(media.url);
      h.attachMedia(v);
    });
    return () => {
      dead = true;
      clearTimeout(timer);
      hlsRef.current?.destroy();
      hlsRef.current = null;
    };
  }, [media, gw]);

  const inIntro = introStart !== null && now >= introStart;
  const f = inIntro ? introFrame(reducedMotion ? timing.playingAt : now - introStart!, EARTH_VIEW, target ?? EARTH_VIEW, timing) : null;
  const playing = startAt !== null && now >= startAt;
  // Server-authoritative playback: start at startAt, then keep the position on the server timeline —
  // small drift is absorbed by a ±5 % rate change, large drift (stall, tab in background) by one seek.
  useEffect(() => {
    const v = video.current;
    if (!v || !playing || !media) return;
    const pos = expectedPosition(gw.serverNowMs(), media.startAt, media.durationMs);
    if (pos * 1000 >= media.durationMs) return;
    const a = v.paused ? { kind: 'seek' as const, to: pos } : syncAction(pos, v.currentTime);
    if (a.kind === 'seek') {
      if (Math.abs(v.currentTime - a.to) > 0.05) v.currentTime = a.to;
      if (!v.paused) setSync((x) => ({ ...x, seeks: x.seeks + 1 }));
    } else if (a.kind === 'rate' && Math.abs(v.playbackRate - a.rate) > 0.001) v.playbackRate = a.rate;
    if (v.paused) void v.play().catch(() => undefined);
    const vp = v.paused ? null : v.currentTime;
    setSync((x) => (Math.abs(x.drift - (pos - v.currentTime)) > 0.02 || x.rate !== v.playbackRate || x.videoPos !== vp ? { ...x, drift: Math.round((pos - v.currentTime) * 100) / 100, rate: v.playbackRate, videoPos: vp } : x));
    // Re-evaluated every ~250 ms via the clock tick below.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [playing, media, gw, Math.floor(now / 250)]);
  const position = playing && media ? expectedPosition(now, media.startAt, media.durationMs) : null;
  // Reveal by what is on screen (the video position), falling back to the server timeline while buffering.
  const shown = media && position !== null ? revealed(media.events, sync.videoPos ?? position) : null;
  const shownCount = shown ? shown.counted : null;
  useEffect(() => {
    onLiveCount?.(shownCount);
  }, [shownCount, onLiveCount]);

  const status =
    round.state === 'BETTING_OPEN'
      ? t('live.status.betting')
      : ['BETTING_CLOSED', 'CAPTURING'].includes(round.state)
        ? t('live.status.capturing')
        : round.state === 'VERIFYING' || (round.state === 'RESULT_FINAL' && !inIntro)
          ? t('live.status.preparing')
          : round.state === 'VOIDED' || round.state === 'VOIDING'
            ? t('live.status.voided')
            : null;
  const videoNote = playing && videoState !== 'OK' ? t(videoState === 'FAILED' ? 'live.status.videoUnavailable' : 'live.status.retrying') : null;
  const center = f?.center ?? target ?? EARTH_VIEW;
  const zoom = f?.zoom ?? ZOOM.country * 0.6;
  const videoOpacity = f?.videoOpacity ?? (playing ? 1 : 0);
  const pulse = ((now % 1600) / 1600) * (reducedMotion ? 0 : 1);
  return (
    <section className="live-stage" data-testid="live-stage" data-phase={f?.phase ?? (playing ? 'PLAYING' : 'IDLE')} data-recoveries={recoveries} aria-label={t('live.stage')}>
      {/* Once the footage fully covers the stage the globe is not rendered at all (no hidden drawing). */}
      {videoOpacity < 1 && (
        <div className="live-globe" style={{ opacity: 1 - videoOpacity }} data-testid="live-globe">
          <Globe center={center} zoom={zoom} target={target} markerOpacity={f ? f.markerOpacity : 1} countryOpacity={f ? f.countryOpacity : 1} pulse={pulse} label={lines.title.join(', ')} />
        </div>
      )}
      <video
        ref={video}
        className="live-video"
        muted
        playsInline
        preload="auto"
        style={{ opacity: videoOpacity }}
        data-testid="live-video"
        aria-label={t('live.video')}
        onLoadedMetadata={(e) => setDims({ w: e.currentTarget.videoWidth, h: e.currentTarget.videoHeight })}
      />
      {/* The locked counting line and zone of this round (from the manifest), drawn on the footage. */}
      {playing && dims && dims.w > 0 && round.manifest.line && round.manifest.roi && (
        <svg className="live-overlay" viewBox={`0 0 ${dims.w} ${dims.h}`} preserveAspectRatio="xMidYMid meet" style={{ opacity: videoOpacity }} data-testid="live-overlay" aria-hidden>
          <polygon className="live-roi" points={round.manifest.roi.map((q) => `${q.x * dims.w},${q.y * dims.h}`).join(' ')} />
          {/* key: the hit animation restarts on every counted vehicle */}
          <g key={shownCount ?? 0} className={shownCount ? 'live-line hit' : 'live-line'}>
            <line x1={round.manifest.line.a.x * dims.w} y1={round.manifest.line.a.y * dims.h} x2={round.manifest.line.b.x * dims.w} y2={round.manifest.line.b.y * dims.h} pathLength={1} />
          </g>
          {(() => {
            // Approach zone and chevrons in the counted direction (display only; the line counts).
            const l = round.manifest.line;
            const z = lineZone(l, dims.w, dims.h);
            const W = dims.w;
            const H = dims.h;
            return (
              <g className="live-zone">
                <polygon points={`${z.near.a.x * W},${z.near.a.y * H} ${z.near.b.x * W},${z.near.b.y * H} ${l.b.x * W},${l.b.y * H} ${l.a.x * W},${l.a.y * H}`} />
                {z.chevrons.map((c, i) => (
                  <polyline key={i} className="live-chev" points={chevronPoints(c, W, H).map((p) => p.join(',')).join(' ')} />
                ))}
              </g>
            );
          })()}
        </svg>
      )}
      {f && f.flash > 0 && <div className="live-flash" style={{ opacity: f.flash }} aria-hidden />}
      {(f ? f.infoOpacity > 0 : !playing) && (
        <div className="live-card" style={{ opacity: f ? f.infoOpacity : 1 }} data-testid="live-card">
          {lines.title.map((l) => (
            <div key={l} className="live-title">
              {l}
            </div>
          ))}
          {lines.detail.map((l) => (
            <div key={l} className="live-detail">
              {l}
            </div>
          ))}
          <div className="live-badge">{t('live.badge')}</div>
          {cam.calibrationStatus === 'PROVISIONAL' && <div className="live-note">{t('live.provisional')}</div>}
          {cam.locationAttribution && <div className="live-note">{cam.locationAttribution}</div>}
        </div>
      )}
      {shown && (
        <div className="live-count" data-testid="live-count" aria-live="polite">
          <span className="live-count-label">{t('live.count')}</span>
          <strong>{shown.counted}</strong>
        </div>
      )}
      {playing && media && (
        <span className="live-sync" data-testid="live-sync" data-drift={sync.drift} data-rate={sync.rate} data-seeks={sync.seeks} aria-hidden>
          Δ {sync.drift.toFixed(2)} s · ×{sync.rate.toFixed(3)}
        </span>
      )}
      {videoNote && (
        <p className="live-status" role="status" data-testid="live-video-state" data-state={videoState}>
          {videoNote}
        </p>
      )}
      {status && !inIntro && (
        <p className="live-status" role="status" data-testid="live-status">
          {status}
        </p>
      )}
    </section>
  );
}
